import { ChangeEvent } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

const ToggleInput = styled.input`
  ${tw`sr-only`}
`;

/** Named colors callers pass map onto the design tokens. Anything else is used as is. */
const COLOR_MAP: Record<string, string> = {
  red: 'var(--color-black)',
  black: 'var(--color-black)',
  brand: 'var(--color-black)',
  green: 'var(--color-success)',
  success: 'var(--color-success)',
};

const ToggleSlider = styled.span<{ $checked: boolean; $color: string }>`
  ${tw`inline-block relative cursor-pointer rounded-full shrink-0`}
  width: 44px;
  height: 24px;
  background-color: ${({ $checked, $color }) => ($checked ? $color : 'var(--color-border-strong)')};
  transition: background-color var(--transition-normal);

  &:after {
    content: '';
    ${tw`absolute rounded-full bg-white`}
    width: 20px;
    height: 20px;
    top: 2px;
    left: 2px;
    box-shadow: 0 1px 2px rgb(15 23 42 / 0.2);
    transform: translateX(${({ $checked }) => ($checked ? '20px' : '0')});
    transition: transform var(--transition-normal);
  }

  input:focus-visible + & {
    box-shadow: var(--shadow-focus);
  }

  input:disabled + & {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Label = styled.label`
  ${tw`inline-flex items-center`}
`;

interface IToggleSliderProps {
  isActive: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  color?: any;
  inputTestId?: string;
  labelTestId?: string;
  disabled?: boolean;
  /** For a switch with no visible text next to it. */
  ariaLabel?: string;
}

export default function SwitchComponent({
  isActive,
  onChange,
  color = 'brand',
  inputTestId,
  labelTestId,
  disabled,
  ariaLabel,
}: IToggleSliderProps) {
  const resolved = COLOR_MAP[String(color)] ?? String(color);
  return (
    <Label data-testid={labelTestId}>
      <ToggleInput
        type="checkbox"
        role="switch"
        aria-checked={isActive}
        checked={isActive}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={inputTestId}
      />
      <ToggleSlider $checked={isActive} $color={resolved} />
    </Label>
  );
}
